import { Button } from "../account/misc/Button";
import { Spinner } from "../account/misc/Spinner";
import SquareLogo from "../icons/SquareLogo";
import { getDesign, getDesignName, getDesignTimberLink, getRendererUrl, getUpdateItemCount, goToDesign, Pos } from "../../service/PosService";
import { CheckCircleOutlined, DownOutlined } from "@ant-design/icons";
import {Drawer} from "antd";
import { ReviewChangesModal } from "./ReviewChangesModal";
import ReviewChanges from "./ReviewChanges";
import React, { useCallback, useEffect, useState } from "react";
import {useRecoilState, useRecoilValue} from "recoil";
import {memberState, mobileState} from "../../state/atoms";
import { DeltaUpdate, Restaurants, UpdateByDesign } from "../../util/types";
import { DesignDto } from "../../generated/dashboard-client";
import ToastOrangeLogo from "../icons/ToastOrangeLogo";
import CloverLogo from "../icons/CloverLogo";
import OpenIcon from "../icons/OpenIcon";
import LinkIcon from "../icons/LinkIcon";
import HorizontalDotsIcon from "../icons/HorizontalDotsIcon";
import { QuestionCircleOutlined, SyncOutlined } from "@ant-design/icons";
import {getTimeAgo, getTimeAgoFromString} from "../../util/utils";
import TreezLogo from "../icons/TreezLogo";
import PopmenuLogo from "../icons/PopmenuLogo";

interface GenericDesignUpdaterProps {
  loading: boolean;
  posReview: (e: React.MouseEvent<HTMLElement>, designId: string) => void;
  posApproveAll: (e: React.MouseEvent<HTMLElement>, designId: string) => void;
  posApproveAllForDesignIds: (e: React.MouseEvent<HTMLElement>, designIds: string[]) => void;
  posIgnore: (e: React.MouseEvent<HTMLElement>, designId: string) => void;
  restaurants: Restaurants,
  deltaUpdates: UpdateByDesign,
  designs: DesignDto[],
  loadingDesignIds: string[],
  isReviewModal: boolean,
  setReviewModal: React.Dispatch<React.SetStateAction<boolean>>,
  optionsModalDesignId: string,
  setOptionsModalDesignId: React.Dispatch<React.SetStateAction<string>>,
  reviewUpdates: DeltaUpdate[],
  onReviewApprove: (ids: string[]) => void;
  pos: Pos
}

export default function GenericDesignUpdater({
  loading,
  posReview,
  posApproveAll,
  posApproveAllForDesignIds,
  posIgnore,
  restaurants,
  deltaUpdates,
  designs,
  loadingDesignIds,
  isReviewModal,
  setReviewModal,
  optionsModalDesignId,
  setOptionsModalDesignId,
  reviewUpdates,
  onReviewApprove,
  pos
}: GenericDesignUpdaterProps) {

  const [member] = useRecoilState(memberState);
  const mobile = useRecoilValue(mobileState);
  const [hasDesigns, setHasDesigns] = useState(false);
  const [sortedDesigns, setSortedDesigns] = useState([] as DesignDto[]);

  const designHasUpdates = useCallback((designId: string) => {
    return deltaUpdates && deltaUpdates[designId] && !!Object.keys(deltaUpdates[designId]).length;
  }, [deltaUpdates]);

  const designHasUnsolvedUpdates = useCallback((designId: string) => {
    return deltaUpdates && deltaUpdates[designId] && !deltaUpdates[designId].solved && !!Object.keys(deltaUpdates[designId]).length;
  }, [deltaUpdates]);

  useEffect(() => {
    setSortedDesigns([...designs].sort((a) => designHasUpdates(a.id!) ? -1 : 1));
  }, [designs, designHasUpdates]);

  useEffect(() => {
    setHasDesigns(!!designs.length);
  }, [designs]);

  function designIdsWithUpdates() {
    let ids = [] as string[];
    designs.forEach((design: DesignDto) => {
      if (designHasUnsolvedUpdates(design.id!)) {
        ids.push(design.id!);
      }
    })
    return ids;
  }

  const buttons = (designId: string) => <><Button type="Primary" inverted className={'review'}
    onClick={(e) => onPosReview(e, designId)}>Review</Button>
    <Button type="Primary" className={'approve'} onClick={(e) => onPosApproveAll(e, designId)}>Approve</Button>
    <Button type="Quaternary" className={'dismiss'} onClick={(e) => onPosIgnore(e, designId)}>Dismiss</Button></>

  const logo = () => {
    if (pos === Pos.Toast) {
      return <ToastOrangeLogo />
    }
    if (pos === Pos.Clover) {
      return <CloverLogo />;
    }
    if (pos === Pos.Square) {
      return <SquareLogo />;
    }
    if (pos === Pos.Treez) {
      return <TreezLogo />;
    }
    if (pos === Pos.Popmenu) {
      return <PopmenuLogo/>;
    }
  }

  const learnMoreLink = () => {
    if (pos === Pos.Toast) {
      return 'https://musthavemenus27.zohodesk.com/portal/en/kb/articles/syncing-toast-updates-to-a-design-14-6-2023-2';
    } else if (pos === Pos.Square) {
      return 'https://musthavemenus27.zohodesk.com/portal/en/kb/articles/transfer-menu-data-from-square';
    } else if (pos === Pos.Treez) {
      return 'https://musthavemenus27.zohodesk.com/portal/en/kb/articles/transfer-menu-data-from-treez';
    } else if (pos === Pos.Popmenu) {
      return 'https://musthavemenus27.zohodesk.com/portal/en/kb/articles/using-popmenu-data';
    }
  }

  const openDesignBtn = (designId: string) => {
    if (mobile) {
      return <OpenIcon onClick={() => goToDesign(designId)} />
    } else {
      return <Button type="Quaternary" className={'design'} onClick={() => goToDesign(designId)}>Open Design</Button>;
    }
  }

  function onApproveAll(e: React.MouseEvent<HTMLDivElement>) {
    if (member.status !== 1) {
      window.MHM.ProModal.upgrade({memberId: member.id, onSuccess: () => window.location.reload()});
    } else {
      posApproveAllForDesignIds(e, designIdsWithUpdates());
    }
  }

  function onPosReview(e: React.MouseEvent<HTMLElement>, designId: string) {
    if (member.status !== 1) {
      window.MHM.ProModal.upgrade({memberId: member.id, onSuccess: () => window.location.reload()});
    } else {
      posReview(e, designId);
    }
  }

  function onPosIgnore(e: React.MouseEvent<HTMLElement>, designId: string) {
    if (member.status !== 1) {
      window.MHM.ProModal.upgrade({memberId: member.id, onSuccess: () => window.location.reload()});
    } else {
      posIgnore(e, designId)
    }
  }

  function onPosApproveAll(e: React.MouseEvent<HTMLElement>, designId: string) {
    if (member.status !== 1) {
      window.MHM.ProModal.upgrade({memberId: member.id, onSuccess: () => window.location.reload()});
    } else {
      posApproveAll(e, designId);
    }
  }

  return <>
    <section>
      {loading && <div className={'spinner'}><Spinner /></div>}
      {!loading && <>
        <div>
          <div className={'design-updater-container'}>
            <div className={'banner'}>
              <div>
                <div className={'first'}>
                  {logo()}
                  <div className="separator" />
                  <span className={'designs-found-nr'}>{designs.length}</span>
                  <span className={'designs-found'}>connected design{(!designs.length || designs.length > 1) ? 's' : ''}</span>
                </div>
                <div className={'second'}>
                  <a className={'learn-more'} href={learnMoreLink()} target={'_blank'} rel="noreferrer">
                    <QuestionCircleOutlined />
                  </a>
                </div>
              </div>
            </div>
            {hasDesigns && <div className={'updater'}>
              <header>
                <span>Connected Designs</span>
                <span className={'status'}>Updates Available</span>
                <span className={'status'}>Last Update</span>
                <span>
                  {!member.viewOnly &&
                    <div className="approve-all-designs" onClick={(e) => onApproveAll(e)}>
                      <SyncOutlined />
                      <span className="update-text">Update all</span>
                    </div>
                  }
                </span>
              </header>
              <section>
                {sortedDesigns.map(design => <div className={'entry'} key={design.id}>
                  <div className={'menu-name'}>
                    <a href={getDesignTimberLink(design.id!)}>
                      <div className="thumb" style={{ backgroundImage: `url(${getRendererUrl(design.id!)})` }} />
                    </a>
                    <div>
                      <span className="name-text">{getDesignName(designs, design.id!)}</span>
                      <span className="status mobile-status">
                        {designHasUpdates(design.id!) && <>
                          {!deltaUpdates[design.id!].solved && <div className={'status'}>{getUpdateItemCount(deltaUpdates, design.id!)} item updates</div>}
                          {deltaUpdates[design.id!].solved && <div className={'done'}><CheckCircleOutlined /> Done</div>}
                        </>}
                      </span>
                      <span className="last-updated mobile-last-updated">
                        <span>Last Updated {getTimeAgoFromString(design.posUpdatedDate || design.updatedDate || new Date().toString())}</span>
                        {/* {design.lastIntegrationUpdate ? <span>Last Updated {getTimeAgoFromString(design.lastIntegrationUpdate!)}</span> : ''} */}
                      </span>
                    </div>
                  </div>

                  {!designHasUpdates(design.id!) &&
                    <div className={'status'}>&mdash; &mdash;</div>}

                  {designHasUpdates(design.id!) && <>
                    {!deltaUpdates[design.id!].solved && <div className={'status'}>{getUpdateItemCount(deltaUpdates, design.id!)} item updates</div>}
                    {deltaUpdates[design.id!].solved && <div className={'done'}><CheckCircleOutlined /> Done</div>}
                  </>}


                  <div className="last-updated status">
                    {getTimeAgo(design)}
                    {/* {design.lastIntegrationUpdate ? <span>{getTimeAgoFromString(design.lastIntegrationUpdate!)}</span> : ""} */}
                  </div>

                  {!designHasUpdates(design.id!) &&
                    <div className={'buttons empty'}>
                      {openDesignBtn(design.id!)}
                    </div>}

                  {designHasUpdates(design.id!) && <>
                    {!mobile && <div className={'buttons'}>
                      {loadingDesignIds.includes(design.id!) && <div className={'spinner'}><Spinner /></div>}
                      {!loadingDesignIds.includes(design.id!) && <>
                        {!deltaUpdates[design.id!].solved && <>{buttons(design.id!)}</>}
                        {deltaUpdates[design.id!].solved && <>{openDesignBtn(design.id!)}</>}
                      </>}
                    </div>}

                    {mobile && <>
                      {deltaUpdates[design.id!].solved && <div onClick={() => goToDesign(design.id!)}><LinkIcon /></div>}
                      {!deltaUpdates[design.id!].solved && <div onClick={() => setOptionsModalDesignId(design.id!)}><HorizontalDotsIcon /></div>}
                    </>}
                  </>}
                </div>)}
              </section>


              {/*<section>*/}
              {/*  {Object.keys(deltaUpdates).map(designId => <div className={'entry'} key={designId}>*/}
              {/*    <div className={'menu-name'}>*/}
              {/*      <a href={getDesignTimberLink(designId)}><img alt={designId}*/}
              {/*                                                   src={`${env.rendererPath}/design/thumbnail/${designId}?height=110`}/></a>*/}
              {/*      <span>{getDesignName(designs, designId)}</span>*/}
              {/*    </div>*/}


              {/*    {!deltaUpdates[designId].solved && <div>{getUpdateItemCount(deltaUpdates, designId)} items</div>}*/}
              {/*    {deltaUpdates[designId].solved && <div className={'done'}><CheckCircleOutlined/> Done</div>}*/}

              {/*    {!mobile && <div className={'buttons'}>*/}
              {/*      {loadingDesignId === designId && <Spinner/>}*/}
              {/*      {loadingDesignId !== designId && <>*/}
              {/*        {!deltaUpdates[designId].solved && <>{buttons(designId)}</>}*/}
              {/*        {deltaUpdates[designId].solved && <>{openDesignBtn(designId)}</>}*/}
              {/*      </>}*/}
              {/*    </div>}*/}


              {/*    {mobile && <>*/}
              {/*      {deltaUpdates[designId].solved && <div onClick={() => goToDesign(designId)}><LinkIcon/></div>}*/}
              {/*      {!deltaUpdates[designId].solved && <div onClick={() => setOptionsModalDesignId(designId)}><HorizontalDotsIcon/></div>}*/}
              {/*    </>}*/}
              {/*  </div>)}*/}
              {/*</section>*/}
            </div>}
          </div>
        </div>
      </>}
    </section>

    {mobile &&
      <Drawer placement={"bottom"} visible={!!optionsModalDesignId} onClose={() => setOptionsModalDesignId('')} closeIcon={<DownOutlined />}
        className={"mobile-bottom-menu options"} height={237}>
        <div className={'container'}>
          <span className={'name'}>{getDesignName(designs, optionsModalDesignId)}</span>
          {optionsModalDesignId && <span className={'last-updated'}>Last Updated {getTimeAgo(getDesign(designs, optionsModalDesignId)!)}</span>}
          <span className={'separator'}>&nbsp;</span>
          <span className={'item-count'}>{getUpdateItemCount(deltaUpdates, optionsModalDesignId)} items available for update</span>
          <div className={'buttons'}>
            {loadingDesignIds.includes(optionsModalDesignId) && <Spinner />}
            {!loadingDesignIds.includes(optionsModalDesignId) && <>
              {deltaUpdates[optionsModalDesignId] && !deltaUpdates[optionsModalDesignId].solved && <>{buttons(optionsModalDesignId)}</>}
            </>}
          </div>
        </div>
      </Drawer>}

    {isReviewModal && !mobile &&
      <ReviewChangesModal onClose={() => setReviewModal(false)} updates={reviewUpdates} onApprove={onReviewApprove} />}
    {isReviewModal && mobile && <Drawer placement={"bottom"} visible={true} onClose={() => setReviewModal(false)} closeIcon={<DownOutlined />}
      className={"mobile-bottom-menu review"} height={'80%'}>
      <ReviewChanges updates={reviewUpdates} onApprove={onReviewApprove} />
    </Drawer>}
  </>
}
